import React from 'react';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { useFormContext } from 'react-hook-form';
import { QuestionData } from '../types';

export const formatPhoneValue = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

interface TextFieldProps {
  question: QuestionData;
  placeholder?: string;
  disabled?: boolean;
}

const TextFieldComponent: React.FC<TextFieldProps> = ({
  question,
  placeholder,
  disabled,
}) => {
  const { register } = useFormContext();
  const registration = register(question.slug);
  const format = question.validations?.format;
  const isPhone = format === 'phone';
  const inputType = isPhone ? 'tel' : 'text';

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isPhone) {
      event.target.value = formatPhoneValue(event.target.value);
    }

    void registration.onChange(event);
  };

  return (
    <Input
      {...registration}
      id={question.slug}
      type={inputType}
      placeholder={placeholder}
      className="w-full"
      disabled={disabled}
      inputMode={isPhone ? 'numeric' : undefined}
      maxLength={isPhone ? 14 : undefined}
      onChange={handleChange}
    />
  );
};

const createTextSchema = (question: QuestionData): z.ZodTypeAny => {
  let schema = z.string();
  const v = question.validations || {};

  // Apply length validations first
  if (v.minLength) {
    schema = schema.min(
      v.minLength,
      `Minimum ${v.minLength} characters required`
    );
  }
  if (v.maxLength) {
    schema = schema.max(
      v.maxLength,
      `Maximum ${v.maxLength} characters allowed`
    );
  }
  if (v.pattern && v.format !== 'email') {
    schema = schema.regex(new RegExp(v.pattern), 'Invalid format');
  }
  if (v.format === 'email') {
    schema = schema.email('Enter a valid email address');
  }

  // Handle required validation
  if (question.required) {
    // If we already have a minLength, don't add another min(1)
    if (!v.minLength) {
      schema = schema.min(1, 'This field is required');
    }
    return schema;
  }

  return z.union([z.literal(''), schema]).optional();
};

const getDefaultValue = (): string => '';

export const TextField = {
  component: TextFieldComponent,
  createSchema: createTextSchema,
  getDefaultValue: () => getDefaultValue(),
};
